import { AlertTriangle } from 'lucide-react'
import { FILING_DOCUMENT_PROCESSING_STATUSES } from '@klear/constants'
import { isFilingDocument } from '@klear/schema/document-review-step'
import { Button } from '@klear/ui/components/button'
import { duplicateRecords, filedRecords, type FiledRecord } from '@/app/document-review/filed-records'
import { PaymentLink, RecordLink } from '@/app/document-review/record-link'
import { proposalKindSentenceLabel } from '@/app/document-review/review-labels'
import { decidedLine, nameOf, type PeopleNames } from '@/app/document-review/review-people'
import type { DocumentReviewDetailOutput } from '@/app/document-review/use-document-review'

type OutcomeProps = {
  detail: DocumentReviewDetailOutput
  names: PeopleNames | null
}

export type OutcomePanelProps = OutcomeProps & {
  canEdit: boolean
  isProcessing: boolean
  isFiling: boolean
  onReprocess: () => void
  onFileAsUpload: () => void
}

function DecidedLine({ decision, detail, names }: OutcomeProps & { decision: string }) {
  return (
    <p className="text-muted-foreground min-h-4 text-xs">
      {names === null ? null : decidedLine(decision, detail, names)}
    </p>
  )
}

function filedHeading(records: FiledRecord[], duplicates: number): string {
  const filed = records.filter(({ proposal }) => isFilingDocument(proposal.record)).length
  if (records.length === 0 && duplicates > 0) return 'Already on file'
  if (records.length > 0 && records.every(({ proposal }) => proposal.recordDeleted)) return 'Record deleted'
  return filed === 1 ? 'Filed on 1 record' : `Filed on ${filed} records`
}

function filedAction({ proposal, joined }: FiledRecord): string {
  if (!joined) return 'Created'
  if (proposal.record?.kind === 'attach_payment') return 'Filed on'
  return isFilingDocument(proposal.record) ? 'Joined' : 'Updated'
}

export function UploadOutcome({ detail, names }: OutcomeProps) {
  return (
    <div className="bg-card shrink-0 rounded-lg border p-4">
      <DecidedLine decision="Filed" detail={detail} names={names} />
      <h2 className="mt-0.5 text-base font-medium">Filed as an upload</h2>
      <p className="text-muted-foreground mt-2 text-sm">On the trader’s Documents tab.</p>
    </div>
  )
}

export function FiledOutcome({ detail, names }: OutcomeProps) {
  const records = filedRecords(detail)
  const duplicates = duplicateRecords(detail)
  const warnings = detail.execution?.warnings ?? []

  return (
    <div className="bg-card shrink-0 rounded-lg border p-4">
      <DecidedLine decision="Filed" detail={detail} names={names} />
      <h2 className="mt-0.5 text-base font-medium">{filedHeading(records, duplicates.length)}</h2>

      {records.length === 0 && duplicates.length === 0 ? (
        <p className="text-muted-foreground mt-2 text-sm">The run made no records of its own.</p>
      ) : (
        <ul className="mt-3 divide-y border-t">
          {records.map((record) => {
            const { proposal, amount } = record
            return (
              <li key={proposal.ordinal} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-2 text-sm">
                <span className="text-muted-foreground w-16 shrink-0 text-xs">{filedAction(record)}</span>
                {record.paymentId === null ? (
                  <RecordLink
                    recordIdDisplay={proposal.recordIdDisplay}
                    orderEventId={record.orderEventId}
                    deleted={proposal.recordDeleted}
                  />
                ) : (
                  <PaymentLink
                    recordIdDisplay={proposal.recordIdDisplay}
                    paymentId={record.paymentId}
                    deleted={proposal.recordDeleted}
                  />
                )}
                <span className="text-muted-foreground">{proposalKindSentenceLabel(proposal.kind)}</span>
                {amount !== null && <span className="ml-auto font-mono tabular-nums">{amount}</span>}
              </li>
            )
          })}
          {duplicates.map(({ proposal, orderEventId }) => (
            <li key={proposal.ordinal} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-2 text-sm">
              <span className="text-muted-foreground w-16 shrink-0 text-xs">Duplicate</span>
              <RecordLink
                recordIdDisplay={proposal.recordIdDisplay}
                orderEventId={orderEventId}
                deleted={proposal.recordDeleted}
              />
            </li>
          ))}
        </ul>
      )}

      {warnings.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2 border-t pt-3">
          {warnings.map((warning) => (
            <li
              key={`${warning.ordinal}-${warning.orderEventId}`}
              className="flex gap-2 text-xs text-amber-700 dark:text-amber-400"
            >
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>{warning.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function DeniedOutcome({
  detail,
  names,
  canEdit,
  isProcessing,
  isFiling,
  onReprocess,
  onFileAsUpload
}: OutcomePanelProps) {
  const heading = 'Dismissed'
  const neverProcessed = detail.model === null
  const author = names === null ? null : nameOf(names, detail.reviewedBy)
  const filing = detail.runs.find(
    (one) => one.id !== detail.id && (FILING_DOCUMENT_PROCESSING_STATUSES as readonly string[]).includes(one.status)
  )

  return (
    <div className="bg-card shrink-0 rounded-lg border p-4">
      <DecidedLine decision={heading} detail={detail} names={names} />
      <h2 className="mt-0.5 text-base font-medium">{heading}</h2>

      {detail.denialReason !== null && (
        <figure className="mt-2">
          <blockquote className="text-sm italic">&ldquo;{detail.denialReason}&rdquo;</blockquote>
          {author !== null && <figcaption className="text-muted-foreground mt-0.5 text-xs">— {author}</figcaption>}
        </figure>
      )}

      {filing !== undefined ? (
        <p className="text-muted-foreground mt-3 text-xs">Run {filing.run} filed this document.</p>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
          <Button size="sm" disabled={!canEdit || isProcessing || isFiling} onClick={onReprocess}>
            {isProcessing ? 'Queueing…' : neverProcessed ? 'Process' : 'Process again'}
          </Button>
          {detail.traderId !== null && (
            <Button
              variant="outline"
              size="sm"
              disabled={!canEdit || isProcessing || isFiling}
              onClick={onFileAsUpload}
            >
              {isFiling ? 'Filing…' : 'File as an upload'}
            </Button>
          )}
          <span className="text-muted-foreground text-xs">
            {neverProcessed
              ? 'Dismissed before it was processed. This decision stays on the record.'
              : 'A new run processes the document again. This decision stays on the record.'}
          </span>
        </div>
      )}

      {!canEdit && filing === undefined && (
        <p className="text-muted-foreground mt-2 text-xs">
          Processing a document needs source-document edit. Ask a super admin or a CSM.
        </p>
      )}
    </div>
  )
}
